"use client";

import Link from "next/link";
import React, { RefObject } from "react";

import { GAClickLink, linkTracker } from "../linkTracker";

export type ContentLinkProps = {
  href: string;
  children: React.ReactNode;
  contentText: string;
  isExternal?: boolean;
  className?: string;
  prefetch?: boolean;
  target?: string;
  noTracking?: boolean;
  noFollow?: boolean;
  noTextDecoration?: boolean;
  ref?: RefObject<any>;
  rel?: string;
  onClick?: (value?: any) => void;
  trackingData?: GAClickLink;
};

/**
 * ContentLink is a versatile link component that can be used for both internal and external links. It supports tracking link clicks for analytics purposes and allows for various customizations such as styling, target, and rel attributes.
 * @param href The URL the link points to.
 * @param children The content to be displayed inside the link.
 * @param contentText The text used for tracking purposes, representing the content of the link.
 * @param isExternal A boolean indicating whether the link is external. If true, it will render an anchor tag; otherwise, it will use Next.js's Link component.
 * @param className Optional CSS class for styling the link.
 * @param prefetch Whether to prefetch the linked page (only applicable for internal links).
 * @param target The target attribute specifying where to open the linked document (e.g., "_blank" for new tab).
 * @param noTracking If true, disables tracking for this link.
 * @param noFollow If true, adds "nofollow" to the rel attribute to prevent search engines from following the link.
 * @param noTextDecoration If true, applies inline styles to remove text decoration from the link.
 * @param ref A React ref object that can be attached to the link element.
 * @param rel The rel attribute specifying the relationship between the current document and the linked document (e.g., "noopener noreferrer" for security when using target="_blank").
 * @param onClick An optional click handler function that will be called when the link is clicked.
 * @param trackingData An optional object containing additional data for tracking link clicks, such as section title, block index, link type, and insurance context.
 * @returns
 */
export function ContentLink({
  href,
  children,
  isExternal = false,
  className,
  noTracking,
  contentText,

  prefetch,
  target,
  noFollow,
  noTextDecoration,
  ref,
  rel,
  onClick,
  trackingData,
  ...rest
}: ContentLinkProps): React.ReactNode {
  if (
    process.env.NODE_ENV === "development" &&
    (!!trackingData?.sectionBlockIndex || !!trackingData?.sectionTitle) &&
    trackingData?.linkType !== "footer" &&
    !noTracking
  ) {
    console.warn(
      "Section index and title should only be undefined in specific cases, is this the case for " +
        contentText +
        "?",
    );
  }

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);

    if (noTracking) {
      return;
    }

    linkTracker(href, contentText, trackingData, event.currentTarget);
  };

  if (isExternal) {
    return (
      <a
        href={href}
        target={"_self"}
        rel={rel ?? (noFollow ? "noopener noreferrer nofollow" : "noopener noreferrer")}
        style={noTextDecoration ? { textDecoration: "none" } : {}}
        className={className}
        ref={ref}
        onClick={handleClick}
        {...rest}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      href={href}
      prefetch={prefetch}
      target={target}
      style={noTextDecoration ? { textDecoration: "none" } : {}}
      className={className}
      rel={rel ?? (noFollow ? "nofollow" : "")}
      ref={ref}
      onClick={handleClick}
      {...rest}
    >
      {children}
    </Link>
  );
}
